import { formatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Truck, Bike, Phone, MapPin, Clock, ArrowLeft, RefreshCw,
  Settings, Package, Route, X, Play, Brain, Zap, AlertTriangle, Loader2
} from "lucide-react";

export default function DeliveryMapSidebar({
  positions, activeDeliveries, activeTab, setActiveTab,
  selectedDeliverer, routeHistory, setRouteHistory, setSelectedDeliverer,
  dispatchSettings, setDispatchSettings, showDispatchSettings, setShowDispatchSettings,
  savingSettings, saveDispatchSettings, loadRouteHistory, simulateGPS, fetchData, routePoints,
}) {
  return (
    <div className="w-[360px] bg-white border-r flex flex-col" data-testid="map-sidebar">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <a href="/orders" className="text-gray-500 hover:text-gray-700"><ArrowLeft className="w-5 h-5" /></a>
          <h1 className="text-lg font-bold text-gray-900">Mapa de Entregas</h1>
        </div>
        <Button variant="ghost" size="icon" onClick={fetchData}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <div className="flex border-b">
        {[{ key: "pedidos", label: "PEDIDOS" }, { key: "motoboys", label: "MOTOBOYS" }, { key: "ajuda", label: "AJUDA" }].map(tab => (
          <button key={tab.key} className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wide border-b-2 transition-colors ${activeTab === tab.key ? "text-emerald-700 border-emerald-600 bg-emerald-50" : "text-gray-500 border-transparent hover:text-gray-700"}`} onClick={() => setActiveTab(tab.key)} data-testid={`map-tab-${tab.key}`}>{tab.label}</button>
        ))}
      </div>

      <div className="px-4 py-3 border-b flex items-center gap-2">
        <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 font-bold gap-2" data-testid="dispatch-btn"><Truck className="w-4 h-4" /> DESPACHAR</Button>
        <Button variant={dispatchSettings.auto_dispatch_enabled ? "default" : "outline"} size="icon" onClick={() => setShowDispatchSettings(s => !s)} className={dispatchSettings.auto_dispatch_enabled ? "bg-amber-500 hover:bg-amber-600 text-white" : ""} data-testid="dispatch-settings-btn" title="Configurar despacho automático">
          {dispatchSettings.auto_dispatch_enabled ? <Brain className="w-4 h-4" /> : <Settings className="w-4 h-4" />}
        </Button>
      </div>

      {showDispatchSettings && (
        <div className="border-b bg-gray-50 p-4 space-y-4" data-testid="dispatch-settings-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-amber-600" />
              <span className="font-bold text-sm">Despacho Automático (IA)</span>
            </div>
            <button onClick={() => setShowDispatchSettings(false)}><X className="w-4 h-4 text-gray-400" /></button>
          </div>
          <p className="text-xs text-gray-500">
            Quando ativado, o sistema atribui automaticamente o melhor estafeta disponível assim que o pedido estiver pronto.
          </p>
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Modo Automático (IA)</Label>
            <Switch checked={dispatchSettings.auto_dispatch_enabled} onCheckedChange={v => setDispatchSettings(s => ({ ...s, auto_dispatch_enabled: v }))} data-testid="auto-dispatch-toggle" />
          </div>
          {dispatchSettings.auto_dispatch_enabled && (
            <div className="space-y-3 pt-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-gray-600">Priorizar mais próximo</Label>
                <Switch checked={dispatchSettings.prioritize_closest} onCheckedChange={v => setDispatchSettings(s => ({ ...s, prioritize_closest: v }))} />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-gray-600">Priorizar melhor avaliação</Label>
                <Switch checked={dispatchSettings.prioritize_rating} onCheckedChange={v => setDispatchSettings(s => ({ ...s, prioritize_rating: v }))} />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Distância máxima (km)</Label>
                <Input type="number" min="1" max="50" value={dispatchSettings.max_distance_km} onChange={e => setDispatchSettings(s => ({ ...s, max_distance_km: parseFloat(e.target.value) || 10 }))} className="h-8 text-sm mt-1" />
              </div>
              <div className="flex items-start gap-2 bg-amber-50 rounded p-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">Critérios: Proximidade · Avaliação · Disponibilidade · Carga de pedidos</p>
              </div>
            </div>
          )}
          <Button onClick={saveDispatchSettings} disabled={savingSettings} className="w-full h-8 text-sm gap-2">
            <Zap className="w-3.5 h-3.5" />
            {savingSettings ? "Salvando..." : "Salvar configurações"}
          </Button>
        </div>
      )}

      <ScrollArea className="flex-1">
        {activeTab === "pedidos" && (
          <div className="divide-y" data-testid="map-orders-list">
            {activeDeliveries.length === 0 && (
              <div className="p-6 text-center text-gray-400"><Package className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">Sin entregas activas</p></div>
            )}
            {activeDeliveries.map(({ order, deliverer, waiting_dispatch, assign_stale, assign_stale_minutes, operational_status }) => {
              const ops = operational_status || {};
              const hasAlert = assign_stale || ops.delivery_late || ops.gps_stale;
              return (
              <div key={order.id} className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-l-4 ${hasAlert ? "border-red-400 bg-red-50/30" : waiting_dispatch ? "border-amber-400 bg-amber-50/40" : "border-transparent"}`} data-testid={`map-order-${order.id.slice(-6)}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono font-bold text-sm text-emerald-700">#{order.id.slice(-6).toUpperCase()}</span>
                  {assign_stale
                    ? <Badge className="bg-red-100 text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Sem recolha {assign_stale_minutes}min</Badge>
                    : ops.delivery_late
                    ? <Badge className="bg-red-100 text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Entrega atrasada {ops.pickup_minutes}min</Badge>
                    : waiting_dispatch
                    ? <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />A despachar…</Badge>
                    : <Badge className={order.status === "delivering" ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700"}>{order.status === "delivering" ? "Em rota" : "Pronto"}</Badge>
                  }
                </div>
                <p className="text-sm text-gray-700">{order.customer_name || "Cliente"}</p>
                {order.delivery_address && <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{order.delivery_address}</p>}
                {deliverer && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center"><Bike className="w-3 h-3 text-white" /></div>
                    <span className="text-xs font-medium text-gray-600">{deliverer.name}</span>
                    {ops.gps_stale && <span className="text-xs text-red-500 flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />GPS {ops.gps_stale_minutes}min</span>}
                  </div>
                )}
                {waiting_dispatch && !deliverer && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><Brain className="w-3 h-3" />IA a selecionar estafeta…</p>
                )}
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-sm font-bold">{formatPrice(order.total)}</span>
                  <div className="flex items-center gap-2">
                    {order.status === "delivering" && ops.pickup_minutes != null && !ops.delivery_late && (
                      <span className="text-xs text-purple-500 flex items-center gap-0.5"><Clock className="w-3 h-3" />{ops.pickup_minutes}min</span>
                    )}
                    <span className="text-xs text-gray-400">{new Date(order.created_at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {activeTab === "motoboys" && (
          <div className="divide-y" data-testid="map-motoboys-list">
            {positions.length === 0 && (
              <div className="p-6 text-center text-gray-400"><Bike className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">Sin repartidores online</p></div>
            )}
            {positions.map(d => (
              <div key={d.id} className={`px-4 py-3 hover:bg-gray-50 cursor-pointer ${selectedDeliverer === d.id ? "bg-emerald-50 border-l-4 border-emerald-500" : ""}`} onClick={() => loadRouteHistory(d.id)} data-testid={`motoboy-${d.id.slice(-6)}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${d.status === "delivering" ? "bg-emerald-500" : "bg-blue-500"}`}>
                    {d.vehicle === "moto" ? <Bike className="w-5 h-5" /> : <Truck className="w-5 h-5" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-sm">{d.name}</span>
                      <Badge className={d.status === "delivering" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}>{d.status === "delivering" ? "En ruta" : "Disponible"}</Badge>
                    </div>
                    {d.phone && <p className="text-xs text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{d.phone}</p>}
                    {d.lat && d.lng && <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{d.lat.toFixed(4)}, {d.lng.toFixed(4)}</p>}
                    {d.last_update && <p className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(d.last_update).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</p>}
                    {d.gps_stale && <p className="text-xs text-red-600 flex items-center gap-1 mt-0.5"><AlertTriangle className="w-3 h-3" />GPS sem atualização</p>}
                  </div>
                </div>
                {d.current_order && (
                  <div className="mt-2 p-2 bg-gray-50 rounded text-xs"><span className="font-bold">Pedido: </span><span className="text-emerald-700">#{d.current_order.id.slice(-6).toUpperCase()}</span> <span className="ml-1">{d.current_order.customer_name}</span></div>
                )}
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={(e) => { e.stopPropagation(); loadRouteHistory(d.id); }}><Route className="w-3 h-3" /> Ver ruta</Button>
                  <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={(e) => { e.stopPropagation(); simulateGPS(d.id); }}><Play className="w-3 h-3" /> Simular GPS</Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "ajuda" && (
          <div className="p-4 space-y-4 text-sm text-gray-600">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3"><h3 className="font-bold text-blue-800 mb-1">Cómo funciona</h3><p>El mapa muestra posiciones de repartidores en tiempo real. Se actualiza cada 5 segundos.</p></div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3"><h3 className="font-bold text-emerald-800 mb-1">Ver ruta</h3><p>Clic en un repartidor en MOTOBOYS para ver su ruta completa con timestamps. Útil para auditoría.</p></div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3"><h3 className="font-bold text-orange-800 mb-1">Link de rastreamiento</h3><p>Cada pedido tiene un link público para el cliente acompañar la entrega en /track/ORDER_ID</p></div>
          </div>
        )}
      </ScrollArea>

      {routeHistory && (
        <div className="border-t bg-white" data-testid="route-history-panel">
          <div className="px-4 py-2 flex items-center justify-between border-b bg-gray-50">
            <div className="flex items-center gap-2"><Route className="w-4 h-4 text-emerald-600" /><span className="font-bold text-sm">{routeHistory.deliverer?.name}</span><span className="text-xs text-gray-400">{routePoints.length} puntos</span></div>
            <button onClick={() => { setRouteHistory(null); setSelectedDeliverer(null); }}><X className="w-4 h-4 text-gray-400" /></button>
          </div>
          <ScrollArea className="max-h-[200px]">
            <div className="px-4 py-2 space-y-1">
              {routePoints.slice(-15).map((point, idx) => (
                <div key={point.id} className="flex items-center gap-2 text-xs">
                  <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[9px] font-bold">{routePoints.length - 14 + idx}</span>
                  <span className="text-gray-500 w-16">{new Date(point.timestamp).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                  <span className="text-gray-400">{point.lat.toFixed(7)}, {point.lng.toFixed(7)}</span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
